import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import {
  useGetPartnerZonesQuery,
  useGetAllItemsQuery,
} from "../../Slices/roomNateApis";
import { useGetAllZoneQuery } from "../../Slices/roomNateConstantApis";
import { useGlobalAuthContext } from "../../context/AuthContext";
import AddPartnerZonesModal from "./AddPartnerZonesModal";
import PartnersLayout from "./PartnersLayout";
import { baseUrl } from "../../utils/baseUrl";

// ── Overview tab ──────────────────────────────────────────────────────────────
function OverviewTab({ partner, packages }) {
  const packageName = packages?.find((p) => p._id === partner?.partnerPackage)?.name;

  const infoRows = [
    { label: "Domain", value: partner?.domain, mono: true },
    { label: "Email", value: partner?.email || null },
    { label: "Package", value: packageName || null },
    { label: "Floor Plans", value: String(partner?.count ?? 0) },
  ];

  return (
    <Box sx={{ pt: 2.5, maxWidth: 560 }}>
      {partner?.primaryColor && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2.5 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: partner.primaryColor,
              border: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            {partner.primaryColor.toUpperCase()}
          </Typography>
        </Stack>
      )}

      <Stack spacing={2} sx={{ mb: 3 }}>
        {infoRows.map(({ label, value, mono }) => (
          <Box key={label}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.6,
                fontSize: "0.68rem",
                display: "block",
                mb: 0.25,
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="body2"
              color={value ? "text.primary" : "text.disabled"}
              sx={mono ? { fontFamily: "monospace" } : {}}
            >
              {value ?? "—"}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Chip
          label="Auto-approve users"
          size="small"
          color={partner?.autoApproveUser ? "success" : "error"}
          variant={partner?.autoApproveUser ? "filled" : "outlined"}
        />
        <Chip
          label="Guest upload"
          size="small"
          color={partner?.allowGuestUpload ? "success" : "error"}
          variant={partner?.allowGuestUpload ? "filled" : "outlined"}
        />
      </Stack>
    </Box>
  );
}

// ── Layout mini card (used in Zones & Layouts tab) ───────────────────────────
function LayoutMiniCard({ layout }) {
  const imgUrl = layout?.frontViewImage
    ? `${baseUrl}/g${layout.frontViewImage}`
    : null;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          height: 76,
          bgcolor: "grey.50",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {imgUrl ? (
          <Box
            component="img"
            src={imgUrl}
            alt={layout?.name || "Layout"}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <BrokenImageOutlinedIcon sx={{ color: "grey.300", fontSize: 26 }} />
        )}
      </Box>
      <Box sx={{ px: 1, py: 0.75 }}>
        <Tooltip title={layout?.name || "Untitled"}>
          <Typography
            variant="caption"
            noWrap
            sx={{ fontWeight: 500, fontSize: "0.7rem", display: "block" }}
          >
            {layout?.name || "Untitled"}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ── Zone section ─────────────────────────────────────────────────────────────
function ZoneSection({ zone, layouts }) {
  const zoneLayouts = layouts.filter((l) =>
    l.zones?.map((z) => z.toLowerCase()).includes(zone.value?.toLowerCase())
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {zone.label}
        </Typography>
        <Chip
          label={`${zoneLayouts.length} layout${zoneLayouts.length !== 1 ? "s" : ""}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.7rem" }}
        />
      </Stack>

      {zoneLayouts.length === 0 ? (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", pl: 0.25 }}
        >
          No layouts assigned to this zone yet.
        </Typography>
      ) : (
        <Grid container spacing={1.5}>
          {zoneLayouts.map((layout) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={layout._id}>
              <LayoutMiniCard layout={layout} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

// ── Zones & Layouts tab ──────────────────────────────────────────────────────
function ZonesLayoutsTab({ partner, setEditPartnerZones }) {
  const { data: partnerZones, isFetching: zonesFetching } =
    useGetPartnerZonesQuery(partner._id, { skip: !partner._id });

  const { data: layoutsData, isFetching: layoutsFetching } = useGetAllItemsQuery(
    { itemType: 2, activePartner: partner._id },
    { skip: !partner._id }
  );

  const layouts = layoutsData?.items ?? [];
  const displayZones = partnerZones?.filter((z) => z?.value !== "custom") ?? [];
  const isLoading = zonesFetching || layoutsFetching;

  return (
    <Box sx={{ pt: 2.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 3 }}
      >
        <Typography variant="body2" color="text.secondary">
          {isLoading
            ? "Loading…"
            : `${displayZones.length} zone${displayZones.length !== 1 ? "s" : ""} · ${layouts.length} layout${layouts.length !== 1 ? "s" : ""}`}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => setEditPartnerZones(partner)}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          Edit Zones
        </Button>
      </Stack>

      {!isLoading && displayZones.length === 0 && (
        <Box
          sx={{
            p: 4,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.disabled" sx={{ mb: 1.5 }}>
            No zones configured for this partner yet.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setEditPartnerZones(partner)}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Add Zones
          </Button>
        </Box>
      )}

      {!isLoading &&
        displayZones.map((zone, i) => (
          <React.Fragment key={zone._id || zone.value}>
            <ZoneSection zone={zone} layouts={layouts} />
            {i < displayZones.length - 1 && <Divider sx={{ mb: 3 }} />}
          </React.Fragment>
        ))}
    </Box>
  );
}

// ── Main PartnerDetail ────────────────────────────────────────────────────────
export default function PartnerDetail({ partner, packages, onBack }) {
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const [tab, setTab] = React.useState(0);
  const [editPartnerZones, setEditPartnerZones] = React.useState({});

  const { data: globalZonesData, isFetching: globalZonesFetching } =
    useGetAllZoneQuery("", { skip: !currentUserSessionStorage });

  const zonesDataForModal = globalZonesFetching
    ? []
    : [
        ...(globalZonesData || []),
        {
          value: "lounging,dining",
          label: "Living + Dining",
          img: "/images/zones/Working.svg",
          capacity: false,
        },
        {
          value: "custom",
          label: "Manual Item",
          img: "/images/zones/Working.svg",
          capacity: false,
        },
      ];

  return (
    <>
      {/* Zones modal — opens programmatically via setEditPartnerZones */}
      <AddPartnerZonesModal
        editPartner={editPartnerZones}
        zonesData={zonesDataForModal}
        setEditPartner={setEditPartnerZones}
      />

      {/* Back button + partner name */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          sx={{
            mb: 1.5,
            color: "text.secondary",
            textTransform: "none",
            fontSize: "0.875rem",
            pl: 0,
            "&:hover": { bgcolor: "transparent", color: "text.primary" },
          }}
        >
          All Partners
        </Button>

        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
          {partner?.primaryColor && (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: partner.primaryColor,
                border: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
              }}
            />
          )}
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: "-0.01em" }}>
            {partner?.title || "Partner"}
          </Typography>
          {partner?.domain && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "monospace", alignSelf: "flex-end", pb: 0.25 }}
            >
              {partner.domain}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Tab bar */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minHeight: 40,
              fontSize: "0.875rem",
            },
          }}
        >
          <Tab label="Overview" />
          <Tab label="Zones & Layouts" />
          <Tab label="Floor Plans" />
        </Tabs>
      </Box>

      {tab === 0 && <OverviewTab partner={partner} packages={packages} />}
      {tab === 1 && (
        <ZonesLayoutsTab
          partner={partner}
          setEditPartnerZones={setEditPartnerZones}
        />
      )}
      {tab === 2 && (
        <PartnersLayout key={partner._id} partnerId={partner._id} />
      )}
    </>
  );
}
